import { Transition } from "@headlessui/react"
import { Form, Head } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import DeleteUser, {
  type DeleteUserBlockReasonKey,
} from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import OnboardingAddressFieldset from "@/components/onboarding/onboarding-address-fieldset"
import OnboardingCountrySelect from "@/components/onboarding/onboarding-country-select"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { toMessages } from "@/lib/form-errors"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/**
 * OKS-40 — Settings → Profile (own-account, all roles).
 *
 * Non-creators (admin / brand / agency) see name only — preserves
 * the OKS-5 baseline. Creators see name + city + country + a
 * structured shipping-address card (matches OKS-22 onboarding).
 *
 * One Inertia `<Form>` writes both User and ShippingAddress
 * atomically server-side (`Settings::ProfilesController#update`).
 * Address errors arrive flat-namespaced as
 * `errors["shipping_address.recipient_name"]` etc — the
 * `<OnboardingAddressFieldset>` reads that exact shape.
 */

export interface SettingsProfileProps {
  user: {
    id: number
    name: string
    email: string
    city?: string | null
    country?: string | null
    /**
     * OKS-934 — the creator's sizes. Optional because non-creators have no
     * such fields; the FIELDSET below is rendered only under `isCreator`.
     */
    apparel_size?: string | null
    shoe_size?: string | null
    // OKS-999 — the phone of record (PRD D4). Creator branch only; absent for
    // brand/agency users, like the sizes above.
    phone?: string | null
  }
  shipping_address?: {
    recipient_name: string
    street: string
    street_line_2: string | null
    postal_code: string
    city: string
    country: string
  } | null
  delete_account: {
    can_delete: boolean
    block_reason_key: DeleteUserBlockReasonKey | null
  }
}

export default function Profile(props: SettingsProfileProps) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()
  const isCreator = currentUser?.role === "creator"

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [
      {
        title: t("settings.profile.breadcrumb"),
        href: settingsProfilePath(),
      },
    ],
    [t],
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.profile.title")}
            description={t("settings.profile.description")}
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
            data-testid="settings-profile-form"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="name">
                    {t("settings.profile.name_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>

                  <Input
                    id="name"
                    name="name"
                    className="mt-1 block w-full"
                    defaultValue={props.user.name ?? currentUser?.name ?? ""}
                    aria-required="true"
                    aria-invalid={Boolean(errors.name)}
                    autoComplete="name"
                    placeholder={t("settings.profile.name_placeholder")}
                  />

                  <InputError
                    className="mt-2"
                    messages={toMessages(errors.name)}
                  />
                </div>

                {isCreator ? (
                  <>
                    {/*
                     * OKS-348 — Plain `<OnboardingCountrySelect>` +
                     * `<Input name="city">`. The OKS-341 partial-reload
                     * adapter is gone with the geocoded substrate; the
                     * creator picks their own country + city explicitly.
                     */}
                    <CreatorLocationFieldset
                      initialCity={props.user.city ?? ""}
                      initialCountry={props.user.country ?? ""}
                      cityError={errors.city as string | string[] | undefined}
                      countryError={
                        errors.country as string | string[] | undefined
                      }
                      t={t}
                    />

                    {/*
                     * OKS-934 — creator sizes.
                     *
                     * ⚠️ THE SERVER HALF SHIPPED WITHOUT THIS and PO caught it:
                     * `user_params` permitted the columns on the creator branch
                     * and `profile_props` serialised them, and nothing rendered
                     * an input — so AC6 was unimplemented while every size
                     * assertion passed, because the SEED wrote the values
                     * directly. Read half proven, full loop unproven.
                     *
                     * ⚠️ The helper is BINDING COPY (UX §3): it is the only
                     * place a creator learns WHO sees this and WHEN, so it must
                     * state both, and it must stay true if the exposure window
                     * in `Collabs::BrandRowSerializer#creator_sizes_for` ever changes.
                     *
                     * Free text, not a select — sizing is not standardised
                     * across regions or brands and an enum cannot express
                     * "M in tops, L in hoodies" (PRD OQ-1). Both optional, both
                     * clearable to empty.
                     */}
                    <Card data-testid="settings-profile-sizes-card">
                      <CardHeader>
                        <CardTitle className="text-base">
                          {t("settings.profile.sizes.title")}
                        </CardTitle>
                        <CardDescription>
                          {t("settings.profile.sizes.helper")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="settings-apparel-size">
                            {t("settings.profile.sizes.apparel.label")}
                          </Label>
                          <Input
                            id="settings-apparel-size"
                            name="apparel_size"
                            type="text"
                            maxLength={40}
                            defaultValue={props.user.apparel_size ?? ""}
                            placeholder={t(
                              "settings.profile.sizes.apparel.placeholder",
                            )}
                            className="mt-2"
                            data-testid="settings-profile-apparel-size"
                          />
                          <InputError
                            className="mt-2"
                            messages={toMessages(errors.apparel_size)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="settings-shoe-size">
                            {t("settings.profile.sizes.shoe.label")}
                          </Label>
                          <Input
                            id="settings-shoe-size"
                            name="shoe_size"
                            type="text"
                            maxLength={40}
                            defaultValue={props.user.shoe_size ?? ""}
                            placeholder={t(
                              "settings.profile.sizes.shoe.placeholder",
                            )}
                            className="mt-2"
                            data-testid="settings-profile-shoe-size"
                          />
                          <InputError
                            className="mt-2"
                            messages={toMessages(errors.shoe_size)}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    {/*
                     * OKS-999 — the carrier contact number, in its OWN card.
                     *
                     * ⚠️ It was briefly inside the « Tailles » card, and that
                     * was a copy-truth bug rather than a layout preference (PO
                     * walk, frame 06b). That card's description reads "Shared
                     * with a brand only when they're preparing a gift for you"
                     * — which is TRUE of the sizes and FALSE of the phone. The
                     * phone reaches a brand through
                     * `Shipments::AddressPayload`'s gated `contact` block,
                     * under a different rule and a different window, so a
                     * creator reading that heading would have been told the
                     * wrong thing about who can see their number.
                     *
                     * `users.phone` is the phone of record: the collab address
                     * form writes it through, so this is where a creator
                     * corrects it. Without this field the only correction
                     * surface would be another collab form — the "fix it
                     * repeatedly somewhere else" trap the ticket closes.
                     */}
                    <Card data-testid="settings-profile-phone-card">
                      <CardHeader>
                        <CardTitle className="text-base">
                          {t("settings.profile.phone.title")}
                        </CardTitle>
                        <CardDescription>
                          {t("settings.profile.phone.hint")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Label htmlFor="settings-phone">
                          {t("settings.profile.phone.label")}
                        </Label>
                        <Input
                          id="settings-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          maxLength={32}
                          defaultValue={props.user.phone ?? ""}
                          placeholder={t("settings.profile.phone.placeholder")}
                          className="mt-2"
                          data-testid="settings-profile-phone-input"
                        />
                        <InputError
                          className="mt-2"
                          messages={toMessages(errors.phone)}
                          data-testid="settings-profile-phone-error"
                        />
                      </CardContent>
                    </Card>

                    <Card data-testid="settings-profile-shipping-card">
                      <CardHeader>
                        <CardTitle className="text-base">
                          {t("settings.creator.profile.shipping_title")}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <OnboardingAddressFieldset
                          values={{
                            recipient_name:
                              props.shipping_address?.recipient_name,
                            street: props.shipping_address?.street,
                            street_line_2:
                              props.shipping_address?.street_line_2 ??
                              undefined,
                            postal_code: props.shipping_address?.postal_code,
                            city: props.shipping_address?.city,
                            country: props.shipping_address?.country,
                          }}
                          errors={
                            errors as unknown as Record<
                              string,
                              string[] | undefined
                            >
                          }
                          disabled={processing}
                        />
                      </CardContent>
                    </Card>
                  </>
                ) : null}

                <div className="flex items-center gap-4">
                  <Button
                    disabled={processing}
                    data-testid="settings-profile-save"
                  >
                    {t("settings.profile.save")}
                  </Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">
                      {t("settings.profile.saved")}
                    </p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser
          canDelete={props.delete_account.can_delete}
          blockReasonKey={props.delete_account.block_reason_key}
          userEmail={props.user.email}
        />
      </SettingsLayout>
    </AppLayout>
  )
}

/**
 * OKS-348 — Creator-only city + country fieldset. Plain
 * `<OnboardingCountrySelect>` + shadcn `<Input>` over the OKS-22 wire
 * shape (`name="city"` + `name="country"`). The OKS-341 partial-reload
 * resolution adapter is gone.
 *
 * Country defaults to whatever the creator already has set (or empty
 * for never-onboarded — UX Spec §3 Creator profile Empty state).
 * NOT auto-defaulted to "FR" — only the brand-admin Add-candidate
 * dialog applies that bias (PRD Q6).
 */
function CreatorLocationFieldset({
  initialCity,
  initialCountry,
  cityError,
  countryError,
  t,
}: {
  initialCity: string
  initialCountry: string
  cityError?: string | string[]
  countryError?: string | string[]
  t: (key: string, opts?: Record<string, unknown>) => string
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
      <div className="grid gap-2">
        <Label htmlFor="city">
          {t("settings.creator.profile.city_label")}
          <span className="text-destructive" aria-hidden="true">
            {" *"}
          </span>
        </Label>
        <Input
          id="city"
          name="city"
          autoComplete="address-level2"
          defaultValue={initialCity}
          // OKS-348 — HTML5 `required` removed; server-side
          // `User#city` presence is authoritative + produces localized
          // inline `<InputError>` content via Inertia roundtrip
          // (PRD Q5 + UX-Spec §9 #5). Asterisk visual cue on label
          // preserves the required affordance.
          aria-required="true"
          maxLength={120}
          data-testid="settings-profile-city"
          aria-invalid={cityError ? "true" : undefined}
          aria-describedby="settings-profile-city-error"
        />
        <InputError
          id="settings-profile-city-error"
          messages={toMessages(cityError)}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="country">
          {t("settings.creator.profile.country_label")}
          <span className="text-destructive" aria-hidden="true">
            {" *"}
          </span>
        </Label>
        <OnboardingCountrySelect
          id="country"
          name="country"
          defaultValue={initialCountry}
          required={false}
          testId="settings-profile-country"
          errorMessages={toMessages(countryError)}
          errorId="settings-profile-country-error"
        />
        <InputError
          id="settings-profile-country-error"
          messages={toMessages(countryError)}
        />
      </div>
    </div>
  )
}
